import { BoxProps } from '@chakra-ui/react';
import { RecordAuditInfo } from '@monetize/types/common';
import React, { FunctionComponent as FC } from 'react';
import {
  MBox,
  MCenter,
  MDivider,
  MFlex,
  MHeading,
  MStatusIDBox,
  MText,
} from '.';
import { useAuth } from '../../services/auth0';
import { MBackButton } from './MBackButton';
import { MEntityStatusBadge } from './MEntityStatusBadge';
import { RecordAuditInfoPopover } from './RecordAuditInfoPopover';

export interface MSettingsPageHeaderProps {
  title?: string | React.ReactNode;
  tag?: React.ReactNode;
  id?: string;
  displayId?: string;
  divider?: boolean;
  hasBackButton?: boolean;
  children?: React.ReactNode;
  status?: string;
  extraSubtitleParts?: React.ReactNode[];
  containerProps?: BoxProps;
  recordAuditInfo?: RecordAuditInfo;
  copyUrl?: string | boolean;
}

const MSettingsPageHeader: FC<MSettingsPageHeaderProps> = React.forwardRef<
  any,
  MSettingsPageHeaderProps
>(
  (
    {
      title,
      tag,
      id,
      displayId,
      divider = true,
      hasBackButton = false,
      children,
      status,
      extraSubtitleParts,
      containerProps,
      recordAuditInfo,
      copyUrl,
    }: MSettingsPageHeaderProps,
    ref,
  ) => {
    const { tenantId, userId } = useAuth();
    return (
      <MBox w="100%" mb={6} ref={ref} {...containerProps}>
        <MFlex
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          ml={hasBackButton ? -2 : 0}
        >
          <MFlex>
            {hasBackButton && <MBackButton />}

            <MFlex align="center" ml={hasBackButton ? 2 : 0} justify="center">
              <MHeading size="lg">{title}</MHeading>
              {status && <MEntityStatusBadge status={status} />}
              {tag && (
                <MText as="span" mt={1}>
                  {tag}
                </MText>
              )}
            </MFlex>
          </MFlex>

          <MFlex alignItems="center">{children}</MFlex>
        </MFlex>
        <MFlex ml={3.5} mt={0.5} columnGap={2} alignItems="center">
          {id && (
            <MStatusIDBox
              id={id}
              copyUrl={copyUrl}
              mb="0.5"
              displayText={displayId || id}
            />
          )}

          {recordAuditInfo && tenantId && userId && (
            <RecordAuditInfoPopover
              recordAuditInfo={recordAuditInfo}
              tenantId={tenantId}
              userId={userId}
            />
          )}

          {extraSubtitleParts?.map((part, i) => (
            <React.Fragment key={i}>
              {/* Determine if divider should be rendered based on a prior item existing */}
              {(i !== 0 || !!id) && (
                <MCenter m={0} w={0} height={3}>
                  <MDivider
                    m={0}
                    orientation="vertical"
                    borderColor="tGray.darkGrayPurple"
                  />
                </MCenter>
              )}
              {part}
            </React.Fragment>
          ))}
        </MFlex>
        {divider && (
          <MDivider
            mt={2}
            borderWidth="1px"
            borderColor="tBlue.hover"
            w="100%"
          />
        )}
      </MBox>
    );
  },
);

export default MSettingsPageHeader;
